import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useLanguage } from '../contexts/LanguageContext';
import { Menu, X, User, LogOut, Settings, Globe } from 'lucide-react';
import Logo from './Logo';

const Header = ({ transparent = false }) => {
  const { user, logout, isAdmin } = useAuth();
  const { language, toggleLanguage, setSpecificLanguage, t } = useLanguage();
  const navigate = useNavigate();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [isLanguageMenuOpen, setIsLanguageMenuOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const headerClass = transparent 
    ? 'bg-transparent' 
    : 'bg-white shadow-sm border-b border-secondary-200';

  const textClass = transparent ? 'text-white' : 'text-secondary-900';
  const linkClass = transparent 
    ? 'text-white hover:text-primary-200' 
    : 'text-secondary-600 hover:text-primary-500';

  return (
    <header className={`${headerClass} transition-all duration-300 relative z-50`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <Link to={user ? "/dashboard" : "/"} className="flex items-center space-x-2">
            <Logo size="sm" />
            <span className={`text-xl font-bold font-display ${textClass}`}>
              BIZREP
            </span>
          </Link>

          {/* Menu Desktop */}
          <div className="hidden md:flex items-center space-x-8">
            {user ? (
              <>
                <Link to="/dashboard" className={`${linkClass} transition-colors`}>
                  {t('dashboard')}
                </Link>
                <Link to="/products" className={`${linkClass} transition-colors`}>
                  {t('products')}
                </Link>
                <Link to="/conversations" className={`${linkClass} transition-colors`}>
                  {t('conversations')}
                </Link>
                <Link to="/campaigns" className={`${linkClass} transition-colors`}>
                  {t('campaigns')}
                </Link>
                <Link to="/agent-config" className={`${linkClass} transition-colors`}>
                  {t('agentConfig')}
                </Link>

                {/* User Menu */}
                <div className="relative">
                  <button
                    onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                    className={`flex items-center space-x-2 ${linkClass} transition-colors`}
                  >
                    <User className="h-5 w-5" />
                    <span>{user.email}</span>
                  </button>

                  {isUserMenuOpen && (
                    <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg py-1 z-50">
                      <Link
                        to="/settings"
                        className="flex items-center px-4 py-2 text-sm text-secondary-700 hover:bg-secondary-50"
                        onClick={() => setIsUserMenuOpen(false)}
                      >
                        <Settings className="h-4 w-4 mr-2" />
                        {t('settings')}
                      </Link>
                      <button
                        onClick={handleLogout}
                        className="flex items-center w-full px-4 py-2 text-sm text-secondary-700 hover:bg-secondary-50"
                      >
                        <LogOut className="h-4 w-4 mr-2" />
                        {t('logout')}
                      </button>
                    </div>
                  )}
                </div>
              </>
            ) : (
              <>
                <Link to="/pricing" className={`${linkClass} transition-colors`}>
                  {t('pricing')}
                </Link>
                <Link to="/login" className={`${linkClass} transition-colors`}>
                  {t('login')}
                </Link>
                <Link
                  to="/register"
                  className="btn-primary px-4 py-2 text-sm font-medium"
                >
                  {t('startFreeTrial')}
                </Link>
              </>
            )}

            {/* Language Selector */}
            <div className="relative">
              <button
                onClick={() => setIsLanguageMenuOpen(!isLanguageMenuOpen)}
                className={`flex items-center space-x-1 ${linkClass} transition-colors`}
                title="Change Language"
              >
                <Globe className="h-4 w-4" />
                <span className="text-xs font-medium">
                  {language === 'en' ? '🇺🇸' : language === 'pt' ? '🇧🇷' : '🇪🇸'}
                </span>
              </button>

              {isLanguageMenuOpen && (
                <div className="absolute right-0 mt-2 w-40 bg-white rounded-md shadow-lg py-1 z-50">
                  <button
                    onClick={() => {
                      setSpecificLanguage('en');
                      setIsLanguageMenuOpen(false);
                    }}
                    className={`flex items-center w-full px-4 py-2 text-sm hover:bg-secondary-50 ${
                      language === 'en' ? 'text-primary-600 bg-primary-50' : 'text-secondary-700'
                    }`}
                  >
                    <span className="mr-2">🇺🇸</span>
                    English
                  </button>
                  <button
                    onClick={() => {
                      setSpecificLanguage('pt');
                      setIsLanguageMenuOpen(false);
                    }}
                    className={`flex items-center w-full px-4 py-2 text-sm hover:bg-secondary-50 ${
                      language === 'pt' ? 'text-primary-600 bg-primary-50' : 'text-secondary-700'
                    }`}
                  >
                    <span className="mr-2">🇧🇷</span>
                    Português
                  </button>
                  <button
                    onClick={() => {
                      setSpecificLanguage('es');
                      setIsLanguageMenuOpen(false);
                    }}
                    className={`flex items-center w-full px-4 py-2 text-sm hover:bg-secondary-50 ${
                      language === 'es' ? 'text-primary-600 bg-primary-50' : 'text-secondary-700'
                    }`}
                  >
                    <span className="mr-2">🇪🇸</span>
                    Español
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden">
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className={`${textClass} hover:text-primary-500 transition-colors`}
            >
              {isMenuOpen ? (
                <X className="h-6 w-6" />
              ) : (
                <Menu className="h-6 w-6" />
              )}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <div className="md:hidden absolute top-16 left-0 right-0 bg-white shadow-lg border-t border-secondary-200 z-40">
            <div className="px-4 py-2 space-y-1">
              {user ? (
                <>
                  <Link
                    to="/dashboard"
                    className="block px-3 py-2 text-secondary-600 hover:text-primary-500 hover:bg-secondary-50 rounded-md transition-colors"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    {t('dashboard')}
                  </Link>
                  <Link
                    to="/products"
                    className="block px-3 py-2 text-secondary-600 hover:text-primary-500 hover:bg-secondary-50 rounded-md transition-colors"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    {t('products')}
                  </Link>
                  <Link
                    to="/conversations"
                    className="block px-3 py-2 text-secondary-600 hover:text-primary-500 hover:bg-secondary-50 rounded-md transition-colors"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    {t('conversations')}
                  </Link>
                  <Link
                    to="/campaigns"
                    className="block px-3 py-2 text-secondary-600 hover:text-primary-500 hover:bg-secondary-50 rounded-md transition-colors"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    {t('campaigns')}
                  </Link>
                  <Link
                    to="/agent-config"
                    className="block px-3 py-2 text-secondary-600 hover:text-primary-500 hover:bg-secondary-50 rounded-md transition-colors"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    {t('agentConfig')}
                  </Link>
                  <div className="border-t border-secondary-200 pt-2 mt-2">
                    <Link
                      to="/settings"
                      className="block px-3 py-2 text-secondary-600 hover:text-primary-500 hover:bg-secondary-50 rounded-md transition-colors"
                      onClick={() => setIsMenuOpen(false)}
                    >
                      {t('settings')}
                    </Link>
                    <button
                      onClick={() => {
                        handleLogout();
                        setIsMenuOpen(false);
                      }}
                      className="block w-full text-left px-3 py-2 text-secondary-600 hover:text-primary-500 hover:bg-secondary-50 rounded-md transition-colors"
                    >
                      {t('logout')}
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <Link
                    to="/pricing"
                    className="block px-3 py-2 text-secondary-600 hover:text-primary-500 hover:bg-secondary-50 rounded-md transition-colors"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    {t('pricing')}
                  </Link>
                  <Link
                    to="/login"
                    className="block px-3 py-2 text-secondary-600 hover:text-primary-500 hover:bg-secondary-50 rounded-md transition-colors"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    {t('login')}
                  </Link>
                  <Link
                    to="/register"
                    className="block px-3 py-2 bg-primary-500 text-white hover:bg-primary-600 rounded-md transition-colors font-medium"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    {t('startFreeTrial')}
                  </Link>
                </>
              )}
              
              {/* Language selector mobile */}
              <div className="border-t border-secondary-200 pt-2 mt-2">
                <div className="px-3 py-2 text-sm font-medium text-secondary-500">
                  <Globe className="h-4 w-4 inline mr-2" />
                  Idioma / Language
                </div>
                <button
                  onClick={() => {
                    setSpecificLanguage('en');
                    setIsMenuOpen(false);
                  }}
                  className={`flex items-center space-x-2 px-6 py-2 text-sm transition-colors w-full ${
                    language === 'en' 
                      ? 'text-primary-600 bg-primary-50' 
                      : 'text-secondary-600 hover:text-primary-500 hover:bg-secondary-50'
                  }`}
                >
                  <span>🇺🇸</span>
                  <span>English</span>
                </button>
                <button
                  onClick={() => {
                    setSpecificLanguage('pt');
                    setIsMenuOpen(false);
                  }}
                  className={`flex items-center space-x-2 px-6 py-2 text-sm transition-colors w-full ${
                    language === 'pt' 
                      ? 'text-primary-600 bg-primary-50' 
                      : 'text-secondary-600 hover:text-primary-500 hover:bg-secondary-50'
                  }`}
                >
                  <span>🇧🇷</span>
                  <span>Português</span>
                </button>
                <button
                  onClick={() => {
                    setSpecificLanguage('es');
                    setIsMenuOpen(false);
                  }}
                  className={`flex items-center space-x-2 px-6 py-2 text-sm transition-colors w-full ${
                    language === 'es' 
                      ? 'text-primary-600 bg-primary-50' 
                      : 'text-secondary-600 hover:text-primary-500 hover:bg-secondary-50'
                  }`}
                >
                  <span>🇪🇸</span>
                  <span>Español</span>
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;

